// Complete MyCalendar Component - No month/year restrictions

import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import {
  Calendar,
  dayjsLocalizer,
  EventPropGetter,
  Event as RbcEvent,
  View,
} from "react-big-calendar";
import dayjs from "dayjs";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./../style/calenderStyle.css";
import StartsAt from "./../../../assets/images/calender/sand-clock-starts.png";
import EndsAt from "./../../../assets/images/calender/sand-clock-ends.png";
import NoEventImage from "./../../../assets/images/calender/no_event_img.png";
import infoImg from "./../../../assets/images/calender/infoIcon.svg";
import CustomDropdown from "./CustomDropdown";
import { useApi } from "../../../service/ApiContext";
import Loader, { LocalStorage } from "../../../utils/Utils";
import Logger from "../../../utils/Logger";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { EventList } from "../data/redux/action/ProgramDetailAction";
import { setPrograms } from "../data/redux/reducer/ProgramDetailReducer";
import { useLocation, useNavigate } from "react-router-dom";
import {
  setCurrentDates,
  setEventss,
  setSelectedValues,
} from "../data/redux/reducer/eventReducer";
import CommonUtils from "../../../utils/Common_Utils";
import isBetween from "dayjs/plugin/isBetween";
import QuestionTooltip from "../../../components/TooltipComponent";
import { useAlert } from "../../../components/AlertContextComponent";
import GenericModal from "../../learning_room/components/GenericModelComponent";
import clockIcon from "../../../assets/images/calender/clock 1.png";
import flagIcon from "../../../assets/images/calender/event_flag.png";
import sessionClock from "../../../assets/images/calender/session_clock.png";
import sessionurl from "../../../assets/images/calender/session_url.png";
import sessionpass from "../../../assets/images/calender/session_pass.png";

const localizer = dayjsLocalizer(dayjs);
dayjs.extend(isBetween);

interface Event extends RbcEvent {
  event_id: string;
  event_type: string;
  event_title: string;
  event_description: string;
  start_date: any;
  end_date: string;
  end_time: string;
  start_time: string;
  duration: number;
  timezone: string;
  offset: number;
  venue: string;
  max_participants: number;
  current_participants: number;
  registration_deadline: string;
  status: string;
  recurrence: string;
  prerequisites: string[];
  learning_outcomes: string[];
  created_by: any;
  created_at: string;
  updated_at: string;
  program_title: string;
  live_session?: {
    session_url: string;
    session_password: string;
  };
  assessment_duration?: number | string;
  number_of_dimension_questions?: number;
  dimension_descriptive_questions?: any;
  curriculum_list?: any;
  category_list?: any;
  event_duration?: number | string;
}

// Updated YearMonthDropdown component - NO restrictions
const YearMonthDropdown: React.FC<{
  currentDate: dayjs.Dayjs;
  onMonthYearChange: (date: dayjs.Dayjs) => void;
  availableYears: number[];
  isDisabled: boolean;
}> = ({ currentDate, onMonthYearChange, availableYears, isDisabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(currentDate.year());
  const [yearScrollIndex, setYearScrollIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const yearsContainerRef = useRef<HTMLDivElement>(null);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const shortMonths = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const YEARS_TO_SHOW = 5;

  // REMOVED: All month/year restriction logic
  // All months are now always available

  const handleYearMonthSelect = (year: number, month: number) => {
    const newDate = dayjs().year(year).month(month);
    onMonthYearChange(newDate);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedYear(currentDate.year());
    const currentYearIndex = availableYears.indexOf(currentDate.year());
    if (currentYearIndex >= 0) {
      const newIndex =
        Math.floor(currentYearIndex / YEARS_TO_SHOW) * YEARS_TO_SHOW;
      setYearScrollIndex(newIndex);
    }
  }, [currentDate, availableYears]);

  const currentYear = currentDate.year();
  const currentMonth = currentDate.month();

  const getDisplayText = () => {
    const shortMonth = shortMonths[currentMonth];
    const twoDigitYear = currentYear.toString().slice(-2);
    return `${shortMonth}'${twoDigitYear}`;
  };

  const getVisibleYears = () => {
    return availableYears.slice(
      yearScrollIndex,
      yearScrollIndex + YEARS_TO_SHOW,
    );
  };

  const visibleYears = getVisibleYears();

  const handleNextYears = () => {
    if (yearScrollIndex + YEARS_TO_SHOW < availableYears.length) {
      setYearScrollIndex(yearScrollIndex + YEARS_TO_SHOW);
    }
  };

  const handlePrevYears = () => {
    if (yearScrollIndex - YEARS_TO_SHOW >= 0) {
      setYearScrollIndex(yearScrollIndex - YEARS_TO_SHOW);
    }
  };

  const hasNextYears = yearScrollIndex + YEARS_TO_SHOW < availableYears.length;
  const hasPrevYears = yearScrollIndex > 0;

  return (
    <div className="year-month-dropdown-wrapper" ref={dropdownRef}>
      <button
        className="year-month-dropdown-btn"
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        disabled={isDisabled}
      >
        {getDisplayText()}
        <span className={`dropdown-arrow ${isOpen ? "open" : ""}`}>▼</span>
      </button>

      {isOpen && (
        <div className="year-month-dropdown-menu">
          <div className="years-navigation-container">
            <button
              className={`year-nav-arrow ${!hasPrevYears ? "disabled" : ""}`}
              onClick={handlePrevYears}
              disabled={!hasPrevYears}
            >
              ◀
            </button>

            <div className="years-horizontal-container" ref={yearsContainerRef}>
              {visibleYears.map((year) => (
                <button
                  key={year}
                  className={`year-tab ${year === selectedYear ? "active" : ""}`}
                  onClick={() => setSelectedYear(year)}
                >
                  {year}
                </button>
              ))}
            </div>

            <button
              className={`year-nav-arrow ${!hasNextYears ? "disabled" : ""}`}
              onClick={handleNextYears}
              disabled={!hasNextYears}
            >
              ▶
            </button>
          </div>

          <div className="months-grid-container">
            <div className="months-grid">
              {months.map((month, monthIndex) => {
                const isSelected =
                  selectedYear === currentYear && monthIndex === currentMonth;
                return (
                  <button
                    key={monthIndex}
                    className={`month-option ${isSelected ? "selected" : ""}`}
                    onClick={() =>
                      handleYearMonthSelect(selectedYear, monthIndex)
                    }
                  >
                    {month.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CustomHeader = ({ label }: { label: string }) => {
  const dayNames: { [key: string]: string } = {
    Sun: "S",
    Mon: "M",
    Tue: "T",
    Wed: "W",
    Thu: "TH",
    Fri: "F",
    Sat: "SA",
  };

  return (
    <div style={{ textAlign: "center" }}>
      <span
        style={{
          fontWeight: 500,
          fontSize: "13px",
          color: "#717579",
          fontFamily: "Mazzard",
          textTransform: "uppercase",
        }}
      >
        {dayNames[label] || label}
      </span>
    </div>
  );
};

// Event Dot Component
const EventDot: React.FC<{ event: any }> = ({ event }) => {
  const currentDate = useSelector(
    (state: RootState) => state.events.currentDate,
  );
  const currentMonth = currentDate.month();
  const currentYear = currentDate.year();

  const eventsForDay = event.events || [event];

  const currentMonthEvents = eventsForDay.filter((e: Event) => {
    const eventDate = dayjs(e.start_date);
    return (
      eventDate.month() === currentMonth && eventDate.year() === currentYear
    );
  });

  if (currentMonthEvents.length === 0) {
    return null;
  }

  const MAX_DOTS = 2;
  const visibleEvents = currentMonthEvents.slice(0, MAX_DOTS);
  const remainingCount = currentMonthEvents.length - MAX_DOTS;

  const getDotColor = (eventType: string): string => {
    switch (eventType) {
      case "JOZUNA_ASSESSMENT":
        return "#ED8627";
      case "LIVE_SESSION":
        return "#8924B9";
      case "PROGRAM_KICKOFF":
        return "#09BD3C";
      case "TECHNICAL_ASSESSMENT":
        return "#B99A32";
      default:
        return "#0A68AC";
    }
  };

  return (
    <div className="event-dots-container">
      {visibleEvents.map((e: Event, idx: number) => (
        <div
          key={`${e.event_id}_${idx}`}
          className="event-dot"
          style={{ backgroundColor: getDotColor(e.event_type) }}
        />
      ))}
      {remainingCount > 0 && (
        <span className="more-dots">+{remainingCount} more</span>
      )}
    </div>
  );
};

const EventDetailCard: React.FC<{
  event: Event | null;
  onClick: () => void;
  onViewDetails: (event: Event, status: string) => void;
}> = ({ event, onClick, onViewDetails }) => {
  const now = dayjs();

  if (!event) return null;

  let cardClass = "event-card";
  let badgeColor = "";

  switch (event.event_type) {
    case "JOZUNA_ASSESSMENT":
      cardClass += " event-card-join";
      badgeColor = "#ED8627";
      break;
    case "LIVE_SESSION":
      cardClass += " event-card-live";
      badgeColor = "#8924B9";
      break;
    case "PROGRAM_KICKOFF":
      cardClass += " event-card-kickoff";
      badgeColor = "#09BD3C";
      break;
    case "TECHNICAL_ASSESSMENT":
      cardClass += " event-card-technical";
      badgeColor = "transparent";
      break;
    default:
      cardClass += " event-card-default";
      badgeColor = "#0A68AC";
      break;
  }

  const isEventRestricted = (event: Event) => {
    const eventStartDateTime = dayjs(
      `${event.start_date} ${event.start_time}`,
      "YYYY/MM/DD hh:mm A",
    );
    const eventEndDateTime = dayjs(
      `${event.end_date} ${event.end_time}`,
      "YYYY/MM/DD hh:mm A",
    );

    if (now.isAfter(eventStartDateTime) && now.isBefore(eventEndDateTime)) {
      return "IN_PROGRESS";
    }
    if (now.isAfter(eventEndDateTime)) {
      return "COMPLETED";
    }
    return "UPCOMING";
  };

  const status = isEventRestricted(event);

  const handleViewDetail = () => {
    onViewDetails(event, status);
  };

  return (
    <div className={cardClass}>
      {event.program_title && (
        <span
          className="event-badge"
          style={{ backgroundColor: badgeColor, color: "white" }}
        >
          {event.program_title}
        </span>
      )}
      <h3 className="event-title">{event.event_title}</h3>
      {event.event_description && (
        <p className="event-description">
          <QuestionTooltip text={event.event_description} color="transparent" />
        </p>
      )}
      <div className="event-time-row">
        <img src={StartsAt} alt="start" className="event-time-icon" />
        <span className="event-time-label">Starts at:</span>
        <span className="event-time-value">
          {event.start_date} | {event.start_time}
        </span>
        <br></br>
        <img src={EndsAt} alt="end" className="event-time-icon" />
        <span className="event-time-label">Ends by:</span>
        <span className="event-time-value">
          {event.end_date} | {event.end_time}
        </span>
      </div>
      <div className="event-view-details">
        <span className="view-details-text" onClick={handleViewDetail}>
          View Details
        </span>
        <img
          src={infoImg}
          className="view-details-icon"
          onClick={handleViewDetail}
          alt="info"
        />
      </div>
    </div>
  );
};

// Main MyCalendar Component
export const MyCalendar: React.FC = () => {
  const eventRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const storedevents = useSelector((state: RootState) => state.events.events);
  const [selectedProgram, setSelectedProgram] = useState<string[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<string[]>([]);
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const [view, setView] = useState<View>("month");
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const dateRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const api = useApi();
  const listProgramData = useSelector(
    (state: RootState) => state.programs.programs,
  );
  const dispatch = useDispatch();
  const currentDate = useSelector(
    (state: RootState) => state.events.currentDate,
  );
  const location = useLocation();

  // Refs to prevent multiple API calls
  const isInitialized = useRef(false);
  const isFetchingEvents = useRef(false);
  const isProgramsLoading = useRef(false);
  const hasAutoSelected = useRef(false);

  // Helper function to expand multi-day events
  const expandMultiDayEvents = (events: Event[]): Event[] => {
    const expandedEvents: Event[] = [];

    events.forEach((event) => {
      const startDate = dayjs(event.start_date);
      const endDate = dayjs(event.end_date);

      const daysDiff = endDate.diff(startDate, "day");

      if (daysDiff === 0) {
        expandedEvents.push(event);
      } else {
        for (let i = 0; i <= daysDiff; i++) {
          const currentDate = startDate.add(i, "day");
          expandedEvents.push({
            ...event,
            event_id: `${event.event_id}_${i}`,
            start_date: currentDate.format("YYYY/MM/DD"),
            end_date: currentDate.format("YYYY/MM/DD"),
          });
        }
      }
    });

    return expandedEvents;
  };

  // REMOVED: Program date range restrictions - now always show wide range
  const availableYears = useMemo(() => {
    const currentYear = dayjs().year();
    const years: number[] = [];
    // Show a wide range of years (30 years before and 30 years after current)
    for (let year = currentYear - 30; year <= currentYear + 30; year++) {
      years.push(year);
    }
    return years;
  }, []);

  // Group events by date for dot display
  const expandedEvents = useMemo(() => {
    const expanded = expandMultiDayEvents(storedevents);

    const currentMonthExpanded = expanded.filter((event) => {
      const eventDate = dayjs(event.start_date);
      return (
        eventDate.month() === currentDate.month() &&
        eventDate.year() === currentDate.year()
      );
    });

    const groupedMap = new Map();
    currentMonthExpanded.forEach((event) => {
      const dateKey = dayjs(event.start_date).format("YYYY-MM-DD");
      if (!groupedMap.has(dateKey)) {
        groupedMap.set(dateKey, []);
      }
      groupedMap.get(dateKey).push(event);
    });

    const groupedEvents: any[] = [];
    groupedMap.forEach((eventsList, date) => {
      groupedEvents.push({
        id: date,
        event_id: `grouped_${date}`,
        start_date: date,
        end_date: date,
        events: eventsList,
        event_type: "GROUPED",
      });
    });

    return groupedEvents;
  }, [storedevents, currentDate]);

  // Group events by date for the right panel
  const groupedEvents: { [date: string]: Event[] } = {};
  storedevents.forEach((event) => {
    const eventDate = dayjs(event.start_date).format("MMMM D, YYYY");
    if (!groupedEvents[eventDate]) {
      groupedEvents[eventDate] = [];
    }
    groupedEvents[eventDate].push(event);
  });

  const dateKeys = Object.keys(groupedEvents).sort(
    (a, b) =>
      dayjs(a, "MMMM D, YYYY").toDate().getTime() -
      dayjs(b, "MMMM D, YYYY").toDate().getTime(),
  );

  const getProgram = async () => {
    // Prevent multiple simultaneous calls
    if (isProgramsLoading.current) return;
    isProgramsLoading.current = true;

    Loader.showLoading("Loading...");
    api.setAuthToken(LocalStorage.getLocalData("access_token"));
    const institute_id = LocalStorage.getLocalData("institute_id");
    try {
      const response = await api.get<any>(`/eprogram/${institute_id}`);
      if (response.data.error) {
        dispatch(setPrograms([]));
        return;
      }
      if (response.data.programs && response.data.programs.length > 0) {
        dispatch(setPrograms(response.data.programs));
      } else {
        dispatch(setPrograms([]));
      }
    } catch (error) {
      Logger.info("error response", error);
    } finally {
      Loader.dismissLoading();
      isProgramsLoading.current = false;
    }
  };

  const [programInfo, setProgramInfo] = useState<any>(null);
  const [nonProgramInfo, setNonProgramInfo] = useState<{
    numberOfEvents: number;
    startDate: string | null;
    endDate: string | null;
    duration: number;
  }>({
    numberOfEvents: 0,
    startDate: null,
    endDate: null,
    duration: 0,
  });

  // Combined function to fetch and merge events
  const fetchEvents = async (selectedDate: dayjs.Dayjs) => {
    // Prevent multiple simultaneous fetches
    if (isFetchingEvents.current) {
      Logger.info("Already fetching events, skipping...");
      return;
    }
    isFetchingEvents.current = true;

    Loader.showLoading("Loading events...");
    api.setAuthToken(LocalStorage.getLocalData("access_token"));

    const month = selectedDate.format("MM");
    const year = selectedDate.format("YYYY");
    const offset = CommonUtils.findOffsetDuration().offset;

    try {
      let allEvents: Event[] = [];
      let programInfoData = null;
      let nonProgramInfoData = null;

      // 1. Fetch non-program events (ALWAYS fetch these)
      const nonProgramUrl = `/non-program/events?month=${month}&year=${year}&offset_time=${offset}&is_dashboard=false`;
      try {
        const nonProgramResponse = await api.get<any>(nonProgramUrl);
        if (nonProgramResponse.data && nonProgramResponse.data.non_program) {
          const nonProgramEvents =
            nonProgramResponse.data.non_program.events || [];
          allEvents = [...allEvents, ...nonProgramEvents];
          nonProgramInfoData = {
            numberOfEvents:
              nonProgramResponse.data.non_program.number_of_events || 0,
            startDate: nonProgramResponse.data.non_program.start_date,
            endDate: nonProgramResponse.data.non_program.end_date,
            duration: nonProgramResponse.data.non_program.program_duration || 0,
          };
        }
      } catch (error) {
        Logger.info("Non-program events error:", error);
      }

      // 2. Fetch program events (ONLY if programs exist and are selected)
      if (listProgramData.length > 0 && selectedProgramId.length > 0) {
        const programIds = selectedProgramId.join(",");
        const programUrl = `/program/events?program_id=${programIds}&month=${month}&year=${year}&offset_time=${offset}`;

        try {
          const programResponse = await api.get<EventList>(programUrl);
          if (programResponse.data && programResponse.data.events) {
            allEvents = [...allEvents, ...programResponse.data.events];
            programInfoData = {
              program_titles: programResponse.data.program_titles,
              program_descriptions: programResponse.data.program_descriptions,
              number_of_events: programResponse.data.number_of_events,
              start_date: programResponse.data.start_date,
              end_date: programResponse.data.end_date,
              program_duration: programResponse.data.program_duration,
            };
          }
        } catch (error) {
          Logger.info("Program events error:", error);
        }
      }

      // 3. Dispatch ALL merged events at once
      dispatch(setEventss(allEvents));

      // 4. Update info states
      if (programInfoData) {
        setProgramInfo(programInfoData);
      } else {
        setProgramInfo(null);
      }

      if (nonProgramInfoData) {
        setNonProgramInfo(nonProgramInfoData);
      }
    } catch (error) {
      Logger.info("Error fetching events:", error);
      dispatch(setEventss([]));
    } finally {
      Loader.dismissLoading();
      isFetchingEvents.current = false;
    }
  };

  const handleProgramInfoClick = () => {
    if (selectedProgramId.length === 0 || !programInfo) return;

    const isMultiple = programInfo.program_titles?.length > 1;

    setProgramDetails({
      title: isMultiple
        ? `${programInfo.program_titles[0]} +${programInfo.program_titles.length - 1} more`
        : programInfo.program_titles?.[0] || selectedProgram[0],
      description: isMultiple
        ? `Showing combined schedule for ${programInfo.program_titles.length} programs`
        : programInfo.program_descriptions?.[0] || "No description available",
      noOfEvents: programInfo.number_of_events || storedevents.length,
      duration: programInfo.program_duration || 0,
      startDate: programInfo.start_date,
      endDate: programInfo.end_date,
    });

    setProgramModelOpen(true);
  };

  // SINGLE useEffect to handle all initialization
  useEffect(() => {
    // Only run once
    if (isInitialized.current) {
      return;
    }
    isInitialized.current = true;

    // Handle token from URL
    const query = new URLSearchParams(location.search);
    const tokenFromURL = query.get("token");
    if (tokenFromURL) {
      LocalStorage.setLocalData("access_token", tokenFromURL);
    }

    // Initial fetch of events (only once)
    fetchEvents(currentDate);

    // Fetch programs
    if (listProgramData.length === 0) {
      getProgram();
    }
  }, []); // Empty dependency array = runs only once
  const getProgramDisplayText = () => {
    if (selectedProgram.length === 0) {
      return ""; // Don't show anything when no program is selected
    }
    if (selectedProgram.length === 1) {
      return selectedProgram[0];
    }
    return `${selectedProgram[0]} +${selectedProgram.length - 1} more`;
  };
  // Handle program data changes - auto-select programs when loaded
  useEffect(() => {
    // Only run if programs exist and none are selected yet
    if (listProgramData.length > 0 && selectedProgramId.length === 0) {
      // Auto-select "All" programs when programs are loaded
      const allProgramIds = listProgramData.map((p) => p.program_id);
      const allProgramTitles = listProgramData.map((p) => p.program_title);

      // Mark that auto-selection happened
      hasAutoSelected.current = true;

      setSelectedProgramId(allProgramIds);
      setSelectedProgram(allProgramTitles);
      const selectedValues = listProgramData.map((p) => ({
        program_title: p.program_title,
        program_id: p.program_id,
      }));
      dispatch(setSelectedValues(selectedValues));

      // After auto-selecting, fetch events with the new selection
      // But only if we're not already fetching
      if (!isFetchingEvents.current) {
        fetchEvents(currentDate);
      }
    } else if (listProgramData.length === 0) {
      // No programs exist - clear selections
      setSelectedProgramId([]);
      setSelectedProgram([]);
      dispatch(setSelectedValues([]));
    }
  }, [listProgramData]);

  // Handle program selection changes from dropdown
  useEffect(() => {
    // Skip if this is the initial load (handled by the initialization useEffect)
    if (!isInitialized.current) {
      return;
    }

    // Skip ONLY if this is the auto-selection from programs loading
    // This allows subsequent manual selections to trigger fetches
    if (hasAutoSelected.current) {
      // This was the auto-selection, reset the flag and skip fetch
      hasAutoSelected.current = false;
      return;
    }

    // Fetch events for the new selection (manual selection from dropdown)
    fetchEvents(currentDate);
  }, [selectedProgramId]);

  // Handle date changes
  useEffect(() => {
    // Skip if this is the initial load (handled by the initialization useEffect)
    if (!isInitialized.current) {
      return;
    }

    fetchEvents(currentDate);
  }, [currentDate]);

  const handleSelectionChange = (
    selected: { program_title: string; program_id: string }[],
  ) => {
    const titles = selected.map((item) => item.program_title);
    const ids = selected.map((item) => item.program_id);
    const selectedValues = selected.map((item) => ({
      program_title: item.program_title,
      program_id: item.program_id,
    }));
    dispatch(setSelectedValues(selectedValues));
    setSelectedProgram(titles);
    setSelectedProgramId(ids);
  };

  const handleMonthYearChange = (newDate: dayjs.Dayjs) => {
    dispatch(setCurrentDates(newDate));
    setCalendarDate(newDate.toDate());
  };

  const handleSelectEvent = (event: any) => {
    const eventsForDay = event.events || [event];
    const targetEvent = eventsForDay[0];

    const originalEventId = targetEvent.event_id.split("_")[0];
    const originalEvent = storedevents.find(
      (e) => e.event_id === originalEventId,
    );
    if (originalEvent) {
      const eventRef = eventRefs.current[originalEvent.event_id];
      if (eventRef) {
        const scrollContainer = eventRef.closest(".event-cards-container");
        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = eventRef.getBoundingClientRect();
          const offset = elementRect.top - containerRect.top;
          scrollContainer.scrollTo({
            top: scrollContainer.scrollTop + offset - 20,
            behavior: "smooth",
          });
        } else {
          eventRef.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
  };

  const dayPropGetter = (date: Date) => {
    const isToday = dayjs(date).isSame(dayjs(), "day");
    return {
      className: isToday ? "today-highlight" : "",
      style: {
        backgroundColor: isToday ? "#FFC400" : "transparent",
        border: isToday ? "#FFC400" : "none",
      },
    };
  };

  const expandEventToMultipleDays = (event: Event): Event[] => {
    const start = new Date(event.start_date);
    const end = new Date(event.end_date);

    const expandedEvents: Event[] = [];
    let currentDate = new Date(start);

    while (currentDate <= end) {
      expandedEvents.push({
        ...event,
        start_date: currentDate.toISOString(),
        end_date: currentDate.toISOString(),
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return expandedEvents;
  };

  const formattedEvents: Event[] = storedevents.flatMap(
    expandEventToMultipleDays,
  );

  const scrollToNearestEventByDate = (clickedDate: Date) => {
    const clickedDay = dayjs(clickedDate);

    const relevantEvent = formattedEvents.find((event) => {
      const eventStart = dayjs(event.start_date);
      const eventEnd = dayjs(event.end_date);
      return clickedDay.isBetween(eventStart, eventEnd, "day", "[]");
    });

    if (relevantEvent) {
      const eventRef = eventRefs.current[relevantEvent.event_id];
      if (eventRef) {
        const scrollContainer = eventRef.closest(".event-cards-container");
        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = eventRef.getBoundingClientRect();
          const offset = elementRect.top - containerRect.top;
          scrollContainer.scrollTo({
            top: scrollContainer.scrollTop + offset - 20,
            behavior: "smooth",
          });
        } else {
          eventRef.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
      return;
    }

    const nearestEvent = formattedEvents.reduce((closest, event) => {
      const eventStart = dayjs(event.start_date);
      const diffToEvent = Math.abs(clickedDay.diff(eventStart, "day"));
      const diffToClosest = Math.abs(
        clickedDay.diff(dayjs(closest.start_date), "day"),
      );
      return diffToEvent < diffToClosest ? event : closest;
    }, formattedEvents[0]);

    if (nearestEvent) {
      const eventRef = eventRefs.current[nearestEvent.event_id];
      if (eventRef) {
        const scrollContainer = eventRef.closest(".event-cards-container");
        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = eventRef.getBoundingClientRect();
          const offset = elementRect.top - containerRect.top;
          scrollContainer.scrollTo({
            top: scrollContainer.scrollTop + offset - 20,
            behavior: "smooth",
          });
        } else {
          eventRef.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
  };

  const handleShowMore = (date: Date) => {
    setView("month");
    const formattedDate = dayjs(date).format("MMMM D, YYYY");
    setSelectedDate(formattedDate);
    scrollToNearestEventByDate(date);
  };

  const isDisabled =
    listProgramData.length > 0 && selectedProgramId.length === 0;

  const [programModelOpen, setProgramModelOpen] = useState(false);
  const [programDetails, setProgramDetails] = useState<{
    title: string;
    description: string;
    noOfEvents: number;
    duration: number;
    startDate: string;
    endDate: string;
  }>({
    title: "",
    description: "",
    noOfEvents: 0,
    duration: 0,
    startDate: "",
    endDate: "",
  });

  const [eventDetailsModalOpen, setEventDetailsModalOpen] = useState(false);
  const [eventDetails, setEventDetails] = useState<any>({
    title: "",
    eventTitle: "",
    eventDesc: "",
    start_date: "",
    start_time: "",
    end_date: "",
    end_time: "",
    status: "",
    sessionUrl: "",
    sessionPass: "",
    eventDuration: "",
    assessment_duration: "",
    number_of_dimension_questions: 0,
    dimension_descriptive_questions: 0,
    curriculum_list: [],
    category_list: [],
    eventType: "",
    offset: 0,
  });

  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedPass, setCopiedPass] = useState(false);

  const copyToClipboard = (text: string, type: "url" | "pass") => {
    navigator.clipboard.writeText(text);
    if (type === "url") {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } else {
      setCopiedPass(true);
      setTimeout(() => setCopiedPass(false), 2000);
    }
  };

  const handleEventDetailsClick = (event: Event, status: string) => {
    const liveSession = event.live_session || {
      session_url: "",
      session_password: "",
    };
    const eventDuration =
      typeof event.duration === "number"
        ? event.duration.toString()
        : (event.duration as string) || "0";
    const assessmentDuration =
      typeof event.assessment_duration === "number"
        ? event.assessment_duration.toString()
        : (event.assessment_duration as string) || "0";

    setEventDetails({
      title: event.event_type,
      eventTitle: event.event_title,
      eventDesc: event.event_description,
      start_date: event.start_date,
      start_time: event.start_time,
      end_date: event.end_date,
      end_time: event.end_time,
      status: status,
      sessionUrl: liveSession.session_url || "",
      sessionPass: liveSession.session_password || "",
      eventDuration: eventDuration,
      assessment_duration: assessmentDuration,
      number_of_dimension_questions: event.number_of_dimension_questions || 0,
      dimension_descriptive_questions:
        event.dimension_descriptive_questions || 0,
      curriculum_list: event.curriculum_list || [],
      category_list: event.category_list || [],
      eventType: event.event_type,
      offset: event.offset,
    });
    setEventDetailsModalOpen(true);
  };

  const navigate = useNavigate();

  const handleSelectSlot = (slotInfo: any) => {
    const slotDate = dayjs(slotInfo.start).format("YYYY-MM-DD");
    setSelectedDate(dayjs(slotInfo.start).format("MMMM D, YYYY"));

    const matchingEvent = Object.values(groupedEvents)
      .flat()
      .find((event: Event) => {
        const startDate = dayjs(event.start_date).format("YYYY-MM-DD");
        const endDate = dayjs(event.end_date).format("YYYY-MM-DD");
        return dayjs(slotDate).isBetween(startDate, endDate, null, "[]");
      });

    if (matchingEvent) {
      const eventElement = eventRefs.current[matchingEvent.event_id];
      if (eventElement) {
        const scrollContainer = eventElement.closest(".event-cards-container");
        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = eventElement.getBoundingClientRect();
          const offset = elementRect.top - containerRect.top;
          scrollContainer.scrollTo({
            top: scrollContainer.scrollTop + offset - 20,
            behavior: "smooth",
          });
        } else {
          eventElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    } else {
      const dateElement = dateRefs.current[slotDate];
      if (dateElement) {
        const scrollContainer = dateElement.closest(".event-cards-container");
        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = dateElement.getBoundingClientRect();
          const offset = elementRect.top - containerRect.top;
          scrollContainer.scrollTo({
            top: scrollContainer.scrollTop + offset - 20,
            behavior: "smooth",
          });
        } else {
          dateElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
  };

  const handleTodayClick = () => {
    const today = dayjs();
    dispatch(setCurrentDates(today));
    setCalendarDate(today.toDate());
  };

  useEffect(() => {
    const resetCalendarState = () => {
      dispatch(setEventss([]));
      dispatch(setSelectedValues([]));
      dispatch(setCurrentDates(dayjs()));
      setSelectedProgram([]);
      setSelectedProgramId([]);
      setCalendarDate(new Date());
      setProgramInfo(null);
      setProgramDetails({
        title: "",
        description: "",
        noOfEvents: 0,
        duration: 0,
        startDate: "",
        endDate: "",
      });
    };

    resetCalendarState();

    return () => {
      dispatch(setEventss([]));
      dispatch(setSelectedValues([]));
    };
  }, []);

  const convertDurationToMinutes = (duration: string): string => {
    if (!duration) return "0";

    if (duration.includes(":")) {
      const parts = duration.split(":");
      if (parts.length === 2) {
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        const totalMinutes = hours * 60 + minutes;
        return totalMinutes.toString();
      }
    }

    return duration;
  };

  const showDropdown = listProgramData.length > 0;
  const showProgramInfo =
    listProgramData.length > 0 && selectedProgram.length > 0;

  return (
    <div className="calendar-main-container">
      <div className="calendar-layout-container">
        <div className="calendar-left-panel">
          <div
            style={{
              display: "flex",
              marginBottom: "10px",
              justifyContent: "space-between",
            }}
          >
            <div style={{ justifyContent: "space-between" }}></div>
            <div className="calendar-controls">
              <button
                className="today-button"
                onClick={handleTodayClick}
                disabled={isDisabled}
              >
                <span
                  className="today-dot"
                  style={{ backgroundColor: "#ecc440" }}
                ></span>
                Today
              </button>
              <YearMonthDropdown
                currentDate={currentDate}
                onMonthYearChange={handleMonthYearChange}
                availableYears={availableYears}
                isDisabled={isDisabled}
              />
            </div>
          </div>

          <Calendar
            localizer={localizer}
            date={calendarDate}
            events={expandedEvents}
            startAccessor={(event: Event) => new Date(event.start_date)}
            endAccessor={(event: Event) => new Date(event.end_date)}
            style={{ height: "70vh" }}
            components={{
              header: CustomHeader,
              event: EventDot,
              toolbar: () => null,
            }}
            dayPropGetter={dayPropGetter}
            view={view}
            onSelectSlot={handleSelectSlot}
            selectable={true}
            onSelectEvent={handleSelectEvent}
            onShowMore={(events, date) => handleShowMore(date)}
            formats={{
              dateFormat: (date) => date.getDate().toString(),
              dayFormat: "d",
            }}
          />
        </div>

        <div className="calendar-right-panel">
          {showDropdown && (
            <div className="event-list-header">
              <div className="event-list-header-left">
                <h3 className="event-list-program">
                  {getProgramDisplayText()}
                </h3>
                {showProgramInfo && (
                  <div
                    className="program-info-icon"
                    onClick={handleProgramInfoClick}
                  >
                    <span className="program-info-text">View Details</span>
                    <img
                      src={infoImg}
                      alt="program info"
                      className="program-info-img"
                    />
                  </div>
                )}
              </div>
              <CustomDropdown onSelectionChange={handleSelectionChange} />
            </div>
          )}

          {!showDropdown && (
            <div className="event-list-header">
              <div className="event-list-header-left">
                <h3 className="event-list-program">Non-Program Events</h3>
              </div>
            </div>
          )}

          {storedevents.length === 0 ? (
            <div className="no-events-container">
              <img
                src={NoEventImage}
                className="no-events-image"
                alt="No events"
              />
              <h3 className="no-events-title">No Events Yet</h3>
              <p className="no-events-subtitle">
                {listProgramData.length > 0
                  ? "Select a program to view its schedule"
                  : "No events available for the selected period"}
              </p>
            </div>
          ) : (
            <div className="event-cards-container">
              {dateKeys.map((date) => (
                <div key={date} className="event-date-section">
                  {groupedEvents[date].map((event) => (
                    <div
                      key={event.event_id}
                      ref={(el) => (eventRefs.current[event.event_id] = el)}
                    >
                      <EventDetailCard
                        event={event}
                        onClick={() => handleSelectEvent(event)}
                        onViewDetails={handleEventDetailsClick}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Program Information Modal */}
        <GenericModal
          isOpen={programModelOpen}
          title="Program Information"
          onClose={() => setProgramModelOpen(false)}
        >
          <h1 className="program-info-title">{programDetails.title}</h1>
          <p className="program-info-description">
            {programDetails.description}
          </p>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            <div className="program-info-stats">
              <div className="program-info-stat-item">
                <img
                  alt="Flag Icon"
                  src={flagIcon}
                  className="eventClockNextweekicon"
                />
                <span className="stat-label">No. of Events:</span>
                <span className="stat-value">{programDetails.noOfEvents}</span>
              </div>
              <div className="program-info-stat-item">
                <img
                  alt="Clock Icon"
                  src={clockIcon}
                  className="eventClockNextweekicon"
                />
                <span className="stat-label">Duration:</span>
                <span className="stat-value">
                  {programDetails.duration}{" "}
                  {programDetails.duration === 1 ? "day" : "days"}
                </span>
              </div>
            </div>

            <div className="program-info-dates">
              <div className="program-info-date-row">
                <img src={StartsAt} alt="start" className="program-time-icon" />
                <span className="date-label">Starts at:</span>
                <span className="date-value">{programDetails.startDate}</span>
              </div>
              <div className="program-info-date-row">
                <img src={EndsAt} alt="start" className="program-time-icon" />
                <span className="date-label">Ends by:</span>
                <span className="date-value">{programDetails.endDate}</span>
              </div>
            </div>
          </div>
        </GenericModal>

        {/* Event Details Modal */}
        <GenericModal
          isOpen={eventDetailsModalOpen}
          title="Event Information"
          onClose={() => setEventDetailsModalOpen(false)}
        >
          <div>
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "space-between",
              }}
            >
              <span
                className="eventTitleTxt"
                style={{
                  color:
                    eventDetails.title === "LIVE_SESSION"
                      ? "#8924B9"
                      : eventDetails.title === "PROGRAM_KICKOFF"
                        ? "#09BD3C"
                        : eventDetails.title === "JOZUNA_ASSESSMENT" ||
                            eventDetails.title === "CURRICULUM_ASSESSMENT"
                          ? "#ED8627"
                          : eventDetails.title === "TECHNICAL_ASSESSMENT"
                            ? "#B99A32"
                            : "#0A68AC",
                }}
              >
                {eventDetails.eventTitle}
              </span>
            </div>

            <p className="descStyle">{eventDetails.eventDesc}</p>

            <div
              style={{
                display: "flex",
                gap: "5px",
                marginTop: "10px",
                flexWrap: "wrap",
              }}
            >
              {eventDetails.assessment_duration !== "0" && (
                <>
                  <img
                    alt=""
                    src={clockIcon}
                    className="eventClockNextweekicon"
                  />
                  <p className="durationTxt">Duration:</p>
                  <p className="durationTxt" style={{ fontWeight: 700 }}>
                    {`${convertDurationToMinutes(eventDetails.assessment_duration)} minutes`}
                  </p>
                </>
              )}
              <img
                alt="Clock Icon"
                src={StartsAt}
                className="eventClockNextweekicon"
              />
              <p className="durationTxt">Starts at:</p>
              <p className="durationTxt" style={{ fontWeight: 700 }}>
                {`${eventDetails.start_date || ""} | ${eventDetails.start_time || ""}`}
              </p>

              <img
                alt="Clock Icon"
                src={EndsAt}
                className="eventClockNextweekicon"
              />
              <p className="durationTxt">Ends by:</p>
              <p className="durationTxt" style={{ fontWeight: 700 }}>
                {`${eventDetails.end_date || ""} | ${eventDetails.end_time || ""}`}
              </p>

              {["JOZUNA_ASSESSMENT", "CURRICULUM_ASSESSMENT"].includes(
                eventDetails.title,
              ) &&
                eventDetails.assessment_duration && (
                  <>
                    <img
                      alt="Clock Icon"
                      src={clockIcon}
                      className="eventClockNextweekicon"
                    />
                    <p className="durationTxt">Assessment Duration (hh:mm):</p>
                    <p className="durationTxt" style={{ fontWeight: 700 }}>
                      {eventDetails.assessment_duration}
                    </p>
                  </>
                )}
            </div>

            {/* Live session warning */}
            {eventDetails.title === "LIVE_SESSION" &&
              (!eventDetails.sessionUrl || !eventDetails.sessionPass) && (
                <div
                  style={{
                    display: "flex",
                    gap: "5px",
                    marginTop: "10px",
                  }}
                >
                  <img
                    alt="session clock"
                    src={sessionClock}
                    className="eventClockNextweekicon"
                  />
                  <p className="sessionUpdateTxt">
                    {!eventDetails.sessionUrl && !eventDetails.sessionPass
                      ? "Session URL and password are not updated"
                      : !eventDetails.sessionUrl
                        ? "Session URL is not updated"
                        : "Session password is not updated"}
                  </p>
                </div>
              )}

            {/* Live session URL and password */}
            {eventDetails.title === "LIVE_SESSION" &&
              eventDetails.sessionUrl &&
              eventDetails.sessionPass && (
                <div
                  style={{
                    display: "flex",
                    gap: "5px",
                    marginTop: "10px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <img
                    alt="session url"
                    src={sessionurl}
                    className="eventClockNextweekicon"
                  />
                  <p className="durationTxt">Session Url:</p>
                  <p className="sessionUrlTxt">{eventDetails.sessionUrl}</p>
                  <p
                    className="sessionUrlTxt"
                    style={{
                      cursor: "pointer",
                      color: copiedUrl ? "green" : "#099EF1",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      textDecoration: "underline",
                    }}
                    onClick={() =>
                      copyToClipboard(eventDetails.sessionUrl, "url")
                    }
                  >
                    {copiedUrl ? "✓ Copied" : "Copy Link"}
                  </p>

                  <img
                    alt="session clock"
                    src={sessionpass}
                    className="eventClockNextweekicon"
                  />
                  <p className="durationTxt">Session Password:</p>
                  <p className="sessionPassTxt">{eventDetails.sessionPass}</p>
                  <p
                    className="sessionUrlTxt"
                    style={{
                      cursor: "pointer",
                      color: copiedPass ? "green" : "#099EF1",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      textDecoration: "underline",
                    }}
                    onClick={() =>
                      copyToClipboard(eventDetails.sessionPass, "pass")
                    }
                  >
                    {copiedPass ? "✓ Copied" : "Copy Password"}
                  </p>
                </div>
              )}

            {/* JOZUNA_ASSESSMENT table */}
            {["JOZUNA_ASSESSMENT"].includes(eventDetails.title) && (
              <div className="table-container">
                <table className="score-table">
                  <thead>
                    <tr>
                      <th>Objective Question Count</th>
                      <th>Descriptive Question Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div
                          style={{
                            backgroundColor: "#f6f6f6",
                            padding: "10px",
                            borderRadius: "10px",
                            fontFamily: "Mazzard",
                          }}
                        >
                          {eventDetails.number_of_dimension_questions}
                        </div>
                      </td>
                      <td>
                        <div className="cellstyle">
                          {eventDetails.dimension_descriptive_questions}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* CURRICULUM_ASSESSMENT table */}
            {["CURRICULUM_ASSESSMENT"].includes(eventDetails.title) &&
              eventDetails.category_list?.length > 0 && (
                <div className="table-container">
                  <table className="score-table">
                    <thead>
                      <tr>
                        <th>Module</th>
                        <th>Programming</th>
                        <th>Question Count</th>
                        <th>Difficulty Distribution</th>
                        <th>Scoring Scheme</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventDetails.category_list.map(
                        (row: any, index: number) => (
                          <tr key={index}>
                            <td>
                              <div
                                style={{
                                  backgroundColor: "#f6f6f6",
                                  padding: "10px",
                                  borderRadius: "10px",
                                  fontFamily: "Mazzard",
                                }}
                              >
                                {row.category_name}
                              </div>
                            </td>
                            <td>
                              <div
                                style={{
                                  backgroundColor: "#f6f6f6",
                                  padding: "10px",
                                  borderRadius: "10px",
                                  fontFamily: "Mazzard",
                                }}
                              >
                                {row.programming_languages !== ""
                                  ? row.programming_languages
                                  : ""}
                              </div>
                            </td>
                            <td>
                              <div className="cellstyle">{row.count}</div>
                            </td>
                            <td>
                              {row.difficulty_count !== null && (
                                <div className="cellstyle">
                                  Easy={row.difficulty_count.easy}, Medium=
                                  {row.difficulty_count.medium}, Hard=
                                  {row.difficulty_count.hard}
                                </div>
                              )}
                              {row.difficulty_count === null && (
                                <div className="cellstyle">
                                  Easy=0, Medium=0, Hard=0
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="cellstyle">
                                Right answer={row.score_per_correct_answer},
                                Wrong answer={row.score_per_wrong_answer}
                              </div>
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              )}

            {/* TECHNICAL_ASSESSMENT table */}
            {["TECHNICAL_ASSESSMENT"].includes(eventDetails.title) && (
              <div
                style={{
                  maxHeight: "500px",
                  overflowY: "auto",
                  paddingRight: "8px",
                  scrollbarWidth: "thin",
                  scrollbarColor: "#c1c1c1 #f1f1f1",
                }}
              >
                {(eventDetails.number_of_dimension_questions > 0 ||
                  eventDetails.dimension_descriptive_questions > 0) && (
                  <>
                    <div
                      style={{
                        fontFamily: "Mazzard",
                        fontWeight: 600,
                        fontSize: "14px",
                        marginTop: "10px",
                      }}
                    >
                      Jozuna:
                    </div>
                    <div className="table-container">
                      <table className="score-table">
                        <thead>
                          <tr>
                            <th>Objective Question Count</th>
                            <th>Descriptive Question Count</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>
                              <div
                                style={{
                                  backgroundColor: "#f6f6f6",
                                  padding: "10px",
                                  borderRadius: "10px",
                                  fontFamily: "Mazzard",
                                }}
                              >
                                {eventDetails.number_of_dimension_questions}
                              </div>
                            </td>
                            <td>
                              <div className="cellstyle">
                                {eventDetails.dimension_descriptive_questions}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {eventDetails.category_list &&
                  eventDetails.category_list.length > 0 && (
                    <>
                      <div
                        style={{
                          marginBottom: "5px",
                          fontFamily: "Mazzard",
                          fontWeight: 600,
                          fontSize: "14px",
                          marginTop: "16px",
                        }}
                      >
                        Curriculum:
                      </div>
                      <div
                        className="table-container"
                        style={{
                          marginTop: "10px",
                        }}
                      >
                        <table
                          className="score-table"
                          style={{ width: "100%", borderCollapse: "collapse" }}
                        >
                          <thead
                            style={{
                              position: "sticky",
                              top: 0,
                              backgroundColor: "#fff",
                              zIndex: 1,
                            }}
                          >
                            <tr>
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: "left",
                                  borderBottom: "2px solid #e0e0e0",
                                }}
                              >
                                Module
                              </th>
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: "left",
                                  borderBottom: "2px solid #e0e0e0",
                                }}
                              >
                                Question Count
                              </th>
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: "left",
                                  borderBottom: "2px solid #e0e0e0",
                                }}
                              >
                                Difficulty Distribution
                              </th>
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: "left",
                                  borderBottom: "2px solid #e0e0e0",
                                }}
                              >
                                Scoring Scheme
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {eventDetails.category_list.map(
                              (row: any, index: number) => (
                                <tr key={index}>
                                  <td>
                                    <div
                                      style={{
                                        backgroundColor: "#f6f6f6",
                                        padding: "10px",
                                        borderRadius: "10px",
                                        fontFamily: "Mazzard",
                                      }}
                                    >
                                      {row.category_name || "N/A"}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="cellstyle">
                                      {row.count || 0}
                                    </div>
                                  </td>
                                  <td>
                                    {row.difficulty_count ? (
                                      <div className="cellstyle">
                                        Easy=
                                        {row.difficulty_count.easy || 0},
                                        Medium=
                                        {row.difficulty_count.medium || 0},
                                        Hard=
                                        {row.difficulty_count.hard || 0}
                                      </div>
                                    ) : (
                                      <div className="cellstyle">
                                        Easy=0, Medium=0, Hard=0
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <div className="cellstyle">
                                      Right answer=
                                      {row.score_per_correct_answer || 0}, Wrong
                                      answer={row.score_per_wrong_answer || 0}
                                    </div>
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                {eventDetails.curriculum_list &&
                  eventDetails.curriculum_list.length > 0 && (
                    <>
                      <div
                        style={{
                          marginBottom: "10px",
                          fontFamily: "Mazzard",
                          fontWeight: 600,
                          fontSize: "14px",
                          marginTop: "16px",
                        }}
                      >
                        Industry:
                      </div>

                      {(() => {
                        const groupedBySubCategory =
                          eventDetails.curriculum_list.reduce(
                            (acc: Record<string, any[]>, item: any) => {
                              const key =
                                item.sub_category_name || "Uncategorized";
                              if (!acc[key]) {
                                acc[key] = [];
                              }
                              acc[key].push(item);
                              return acc;
                            },
                            {} as Record<string, any[]>,
                          );

                        return Object.keys(groupedBySubCategory).map(
                          (subCategoryName) => {
                            const items = groupedBySubCategory[subCategoryName];
                            return (
                              <div
                                key={subCategoryName}
                                style={{ marginBottom: "20px" }}
                              >
                                <div
                                  style={{
                                    borderRadius: "6px",
                                    fontFamily: "Mazzard",
                                    fontWeight: 600,
                                    fontSize: "14px",
                                  }}
                                >
                                  <span style={{ fontWeight: 400 }}>
                                    Industry Domain:
                                  </span>{" "}
                                  {subCategoryName}
                                </div>

                                <div
                                  className="table-container"
                                  style={{
                                    border: "1px solid #e0e0e0",
                                    borderRadius: "8px",
                                  }}
                                >
                                  <table
                                    className="score-table"
                                    style={{
                                      width: "100%",
                                      borderCollapse: "collapse",
                                    }}
                                  >
                                    <thead
                                      style={{
                                        position: "sticky",
                                        top: 0,
                                        backgroundColor: "#fff",
                                        zIndex: 1,
                                      }}
                                    >
                                      <tr>
                                        <th
                                          style={{
                                            padding: "12px",
                                            textAlign: "left",
                                            borderBottom: "2px solid #e0e0e0",
                                          }}
                                        >
                                          Module
                                        </th>
                                        <th
                                          style={{
                                            padding: "12px",
                                            textAlign: "left",
                                            borderBottom: "2px solid #e0e0e0",
                                          }}
                                        >
                                          Question Count
                                        </th>
                                        <th
                                          style={{
                                            padding: "12px",
                                            textAlign: "left",
                                            borderBottom: "2px solid #e0e0e0",
                                          }}
                                        >
                                          Difficulty Distribution
                                        </th>
                                        <th
                                          style={{
                                            padding: "12px",
                                            textAlign: "left",
                                            borderBottom: "2px solid #e0e0e0",
                                          }}
                                        >
                                          Scoring Scheme
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {items.map((row: any, index: number) => (
                                        <tr key={index}>
                                          <td>
                                            <div
                                              style={{
                                                backgroundColor: "#f6f6f6",
                                                padding: "10px",
                                                borderRadius: "10px",
                                                fontFamily: "Mazzard",
                                              }}
                                            >
                                              {row.category_name || "N/A"}
                                            </div>
                                          </td>
                                          <td>
                                            <div className="cellstyle">
                                              {row.count || 0}
                                            </div>
                                          </td>
                                          <td>
                                            {row.difficulty_count ? (
                                              <div className="cellstyle">
                                                Easy=
                                                {row.difficulty_count.easy || 0}
                                                , Medium=
                                                {row.difficulty_count.medium ||
                                                  0}
                                                , Hard=
                                                {row.difficulty_count.hard || 0}
                                              </div>
                                            ) : (
                                              <div className="cellstyle">
                                                Easy=0, Medium=0, Hard=0
                                              </div>
                                            )}
                                          </td>
                                          <td>
                                            <div className="cellstyle">
                                              Right answer=
                                              {row.score_per_correct_answer ||
                                                0}
                                              , Wrong answer=
                                              {row.score_per_wrong_answer || 0}
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          },
                        );
                      })()}
                    </>
                  )}
              </div>
            )}

            {/* Assessment Button Footer */}
            {eventDetails.status === "IN_PROGRESS" &&
              (eventDetails.eventType === "CURRICULUM_ASSESSMENT" ||
                eventDetails.eventType === "JOZUNA_ASSESSMENT" ||
                eventDetails.eventType === "TECHNICAL_ASSESSMENT") && (
                <div
                  style={{
                    borderTop: "none",
                    display: "flex",
                    justifyContent: "center",
                    fontFamily: "Mazzard",
                    marginTop: "20px",
                    paddingTop: "16px",
                  }}
                >
                  <button
                    style={{
                      backgroundColor: "#FFC400",
                      borderColor: "#FFC400",
                      color: "#272727",
                      fontFamily: "Mazzard",
                      fontWeight: "600",
                      padding: "10px 24px",
                      borderRadius: "8px",
                      border: "none",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/dashboard/assessment")}
                  >
                    Go to assessment
                  </button>
                </div>
              )}
          </div>
        </GenericModal>
      </div>
    </div>
  );
};
